import React from "react";
import { Document, Page, StyleSheet, Text, View, pdf } from "@react-pdf/renderer";
import { toDraft } from "../components/Mentions/MentionText";
import { GOLD_LIGHT } from "../components/PdpAudit/PdpAuditPdf";
import {
    PDF_FONT,
    TdcPdfHeader,
    TdcPdfSectionHeading,
    downloadPdfBlob,
    formatPdfDate,
    pdfExportedStamp,
    pdfFileName,
    pluralCount,
    tdcPdfStyles,
} from "./pdf/tdcPdfTheme";

export interface VideoComment {
    id: number;
    message: string;
    timestamp?: number | null;
    timestamp_end?: number | null;
    edited?: boolean;
    deleted?: boolean;
    created_at?: string;
    guest_name?: string | null;
    user?: { displayName?: string | null } | null;
    file?: { version?: number | null } | null;
}

export interface PdfVideo {
    videoName: string;
    comments: VideoComment[];
}

interface CommentsPDFProps {
    videos: PdfVideo[];
    title: string;
    subtitle?: string;
}

const styles = StyleSheet.create({
    page: tdcPdfStyles.page,
    empty: tdcPdfStyles.empty,
    comment: { flexDirection: "row", marginBottom: 12 },
    time: { width: 78, fontFamily: PDF_FONT.oswaldMedium, fontSize: 9, color: GOLD_LIGHT },
    body: { flex: 1 },
    author: tdcPdfStyles.entryLabel,
    date: tdcPdfStyles.entryMeta,
    message: tdcPdfStyles.entryValue,
});

const toClock = (secs: number): string => {
    const total = Math.max(0, Math.floor(secs));
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const formatTimestamp = (start?: number | null, end?: number | null): string =>
    typeof end === "number" && end > (start ?? 0)
        ? `${toClock(start ?? 0)} - ${toClock(end)}`
        : toClock(start ?? 0);

const authorName = (comment: VideoComment): string =>
    comment.guest_name || comment.user?.displayName || "Unknown";

export const getReviewComments = (
    comments?: VideoComment[] | null,
    { includeDeleted = false }: { includeDeleted?: boolean } = {},
): VideoComment[] =>
    (comments ?? [])
        .filter(
            (comment) =>
                (includeDeleted || !comment.deleted) &&
                (comment.guest_name || comment.user?.displayName),
        )
        .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

const versionOf = (comment: VideoComment): number | null =>
    typeof comment.file?.version === "number" ? comment.file.version : null;

export interface VersionGroup {
    version: number | null;
    comments: VideoComment[];
}

// Oldest version first so the feedback reads in the order it was given;
// comments inside a version keep their timestamp order.
export const groupByVersion = (comments: VideoComment[]): VersionGroup[] => {
    const byVersion = comments.reduce((acc, comment) => {
        const version = versionOf(comment);
        acc.set(version, [...(acc.get(version) ?? []), comment]);
        return acc;
    }, new Map<number | null, VideoComment[]>());
    return [...byVersion.entries()]
        .sort(([a], [b]) => (a ?? Infinity) - (b ?? Infinity))
        .map(([version, list]) => ({ version, comments: list }));
};

const versionLabel = (version: number | null, latest: number | null): string => {
    if (version === null) return "Version unknown";
    return version === latest ? `Version ${version} (latest)` : `Version ${version}`;
};

const CommentRow: React.FC<{ comment: VideoComment }> = ({ comment }) => (
    <View style={styles.comment} wrap={false}>
        <Text style={styles.time}>
            {formatTimestamp(comment.timestamp, comment.timestamp_end)}
        </Text>
        <View style={styles.body}>
            <Text style={styles.author}>
                {authorName(comment)}
                <Text style={styles.date}>
                    {"   "}
                    {formatPdfDate(comment.created_at)}
                    {comment.edited ? " (edited)" : ""}
                    {comment.deleted ? " (deleted)" : ""}
                </Text>
            </Text>
            <Text style={styles.message}>{toDraft(comment.message)}</Text>
        </View>
    </View>
);

const VideoPage: React.FC<{ video: PdfVideo; title: string; subtitle?: string }> = ({
    video,
    title,
    subtitle,
}) => {
    const { comments, videoName } = video;
    const versions = groupByVersion(comments);
    const numbered = versions.map((group) => group.version).filter((v): v is number => v !== null);
    const latest = numbered.length > 0 ? Math.max(...numbered) : null;
    const single = versions.length === 1;

    return (
        <Page size="A4" style={styles.page}>
            <TdcPdfHeader
                title={title}
                subtitle={subtitle}
                meta={pdfExportedStamp(videoName)}
            />

            {comments.length === 0 ? (
                <Text style={styles.empty}>No comments have been left on this video.</Text>
            ) : (
                versions.map(({ version, comments: versionComments }) => (
                    <View key={version ?? "unknown"}>
                        <TdcPdfSectionHeading
                            label={
                                single && version === null
                                    ? videoName
                                    : `${videoName} — ${versionLabel(version, latest)}`
                            }
                            count={pluralCount(versionComments.length, "comment")}
                        />
                        {versionComments.map((comment) => (
                            <CommentRow key={comment.id} comment={comment} />
                        ))}
                    </View>
                ))
            )}
        </Page>
    );
};

const CommentsPDF: React.FC<CommentsPDFProps> = ({ videos, title, subtitle }) => (
    <Document>
        {videos.map((video, index) => (
            <VideoPage key={index} video={video} title={title} subtitle={subtitle} />
        ))}
    </Document>
);

export const commentsPdfBlob = async (props: CommentsPDFProps): Promise<Blob> =>
    pdf(<CommentsPDF {...props} />).toBlob();

export const downloadCommentsPDF = async ({
    comments,
    videoName,
    projectTitle,
    productTitle,
}: {
    comments: VideoComment[];
    videoName: string;
    projectTitle?: string;
    productTitle?: string;
}): Promise<void> => {
    const blob = await commentsPdfBlob({
        videos: [{ videoName, comments: getReviewComments(comments) }],
        title: projectTitle || videoName,
        subtitle: productTitle,
    });

    downloadPdfBlob(blob, pdfFileName(videoName, "comments", "video"));
};
